// ITEM 14(a) — **THE LANGUAGE LAW. ENGLISH AND SPANISH, AND WHAT MAY NEVER BE TRANSLATED AT ALL.**
//
// Imported by BOTH sides — the client paints the language, the server generates in it and gates what
// comes back — so the answer to "what language is this member in" is one answer rather than two.
//
// **IT IMPORTS EXACTLY ONE THING, AND THAT IS DELIBERATE (R13).** `src/ordinals.js` is already this
// house's single answer key for an ordinal, and `src/mechanicalText.js` ENFORCES against it. A second
// copy of that rule in here would be the exact defect W15-1 was written to end — four
// implementations, three behaviours, and the one members read was the one with none. The Spanish
// ordinal is DERIVED beside the English one, in the same module, so there is still one place to look.
//
// **THE PART OF THIS FILE THAT MATTERS MOST IS THE SACRED-VERBATIM LAW**, and it is her order's own
// sentence: conjurations, incantations, verses, divine names and seal text NEVER translate; framing
// translates. A working is not prose about a working. A verse rendered into Spanish by a model is no
// longer a quotation, it is a paraphrase wearing quotation marks, and this house already has a gate
// whose whole job is that distinction (exact-quotation). A divine name is not a word, it is a NAME.
//
// **IT IS WRITTEN AS A BICONDITIONAL, WHICH IS THE FIX SHAPE THIS REPO HAS PAID FOR FIVE TIMES**
// (R28/R29/R30/R32/R33): the parts that translate are DECLARED, the parts that never translate are
// DECLARED, and their union is asserted to be the whole roster. A new part kind then has to join one
// list or the other rather than falling through the gap — and the gap here is the dangerous side,
// because an unrostered part would default to translatable and a psalm would come back paraphrased.

import { ordinal, ordinalSuffix } from "./ordinals.js";

// ---------------------------------------------------------------------------
// THE ROSTER. Two languages. A language not on this list does not exist.
// ---------------------------------------------------------------------------
export const LANGUAGES = ["en", "es"];
export const DEFAULT_LANGUAGE = "en";

// The name of each language IN ITSELF, which is the only honest way to render a language picker: a
// member who reads no English cannot find "Spanish" in a list, and "Español" is legible to everybody.
export const LANGUAGE_ENDONYMS = { en: "English", es: "Español" };
// The name in the house's own copy, for a sentence the SERVER composes about a member.
export const LANGUAGE_NAMES = { en: "English", es: "Spanish" };

// The BCP-47 tag written on <html>. Kept apart from the code because they are not the same fact and
// the day they stop matching (a regional variant, say) a single value would have to serve two jobs.
export const LANGUAGE_TAGS = { en: "en", es: "es" };

// ITEM 6's shape exactly, because language IS a theme-shaped preference and inventing a second
// persistence shape for it is how two preferences end up with two bugs.
export const LANGUAGE_STATE_KEY = "language";      // the key inside `profiles.room_state` (a jsonb MERGE)
export const LANGUAGE_STORAGE_KEY = "12h_lang";    // the device cache, read BEFORE first paint
export const LANGUAGE_ATTR = "lang";               // the attribute on <html>

export function isLanguage(value) {
  return LANGUAGES.includes(String(value ?? ""));
}

// ---------------------------------------------------------------------------
// **WHAT THE HOUSE SPEAKS IS NOT WHAT THE ENGINE MAY WRITE, AND THESE ARE TWO ROSTERS ON PURPOSE.**
// ---------------------------------------------------------------------------
//
// ITEM 14(d)'s measurement, run before a line of it was designed
// (`node scripts/item14d-gate-readiness.mjs` -> `outputs/item14/GATE-READINESS.md`):
// **22 of 27 matched English/Spanish violation pairs are BLIND in Spanish.** The voice gate, the
// register gate, the cliche class, the confidentiality scan and the spoken-word law are English
// regexes, so a Spanish reply passes them by not being readable to them — the gate runs, finds
// nothing, logs nothing, and delivers. That is not a weaker gate. **It is NO GATE**, and the standing
// GATE LAW forbids exactly that: anything carrying Lala's name passes a gate before delivery.
//
// So the INTERFACE speaks both languages today and the ENGINE writes in English only. `LANGUAGES` is
// what a member may choose to read the house in; `GENERATION_LANGUAGES` is what this house is allowed
// to ASK A MODEL FOR. Widening the second one is ITEM 14(d)'s whole job and it is one line — after
// the gates can read the language, not before.
export const GENERATION_LANGUAGES = ["en"];

export function mayGenerateIn(code) {
  return GENERATION_LANGUAGES.includes(String(code ?? ""));
}

// **AN UNKNOWN LANGUAGE IS REFUSED AT THE WRITE DOOR AND RESOLVED ONLY ON A READ** (R44). These are
// two different acts and collapsing them is how a member's typo becomes a silent preference change:
// `isLanguage` is what the route asks before it stores anything, and this is what a READER of an old
// or corrupt row asks so the site still renders.
export function normalizeLanguage(value) {
  return isLanguage(value) ? String(value) : DEFAULT_LANGUAGE;
}

export function languageEndonym(code) {
  return LANGUAGE_ENDONYMS[normalizeLanguage(code)];
}
export function languageName(code) {
  return LANGUAGE_NAMES[normalizeLanguage(code)];
}

// R18's DISTINCTION, and it is load-bearing here for the same reason it was on the seal poll and the
// release window: a row that has NOT ARRIVED and a row that arrived WITHOUT A LANGUAGE are two
// different facts. The first must wait (the account may say Spanish and painting English first is a
// visible flash of the wrong language); the second is an ANSWER and it means English.
export function languageFromState(state) {
  if (!state || typeof state !== "object") return null;   // nothing has arrived — the caller waits
  const raw = state[LANGUAGE_STATE_KEY];
  if (raw === undefined) return DEFAULT_LANGUAGE;         // arrived, said nothing — that IS English
  return normalizeLanguage(raw);
}

// The PARTIAL write. `room_state` is a jsonb MERGE, so a language write says language and nothing
// else — ITEM 6 found the live defect a partial writer trips, where a write that never mentioned a
// background blanked it.
export function languageStatePatch(code) {
  return { [LANGUAGE_STATE_KEY]: normalizeLanguage(code) };
}

// **THE ATTRIBUTE IS WRITTEN FOR BOTH LANGUAGES, ALWAYS** (ITEM 6's rule, and R18's distinction in
// the DOM): if English were the ABSENCE of a lang attribute, "she chose English" and "her preference
// has not loaded" would be the same document. It also has a consequence nothing else on this site
// has: **a screen reader takes its PRONUNCIATION from this attribute**, so Spanish under `lang="en"`
// is read aloud in an English voice, word by word, which is worse than not translating at all.
export function applyLanguage(root, code) {
  if (!root) return DEFAULT_LANGUAGE;
  const lang = normalizeLanguage(code);
  root.setAttribute(LANGUAGE_ATTR, LANGUAGE_TAGS[lang]);
  return lang;
}

export function readStoredLanguage(storage) {
  try {
    const raw = storage?.getItem?.(LANGUAGE_STORAGE_KEY);
    return isLanguage(raw) ? String(raw) : null;   // null, not the default: "nothing cached" is not "English"
  } catch { return null; }
}
export function storeLanguage(storage, code) {
  try { storage?.setItem?.(LANGUAGE_STORAGE_KEY, normalizeLanguage(code)); } catch { /* a full or blocked store is not an error worth showing her */ }
}

// ---------------------------------------------------------------------------
// ORDINALS. The English rule is imported; the Spanish rule is written here because Spanish has one.
// ---------------------------------------------------------------------------
//
// **SPANISH ORDINALS ARE GENDERED AND ENGLISH ORDINALS ARE NOT, WHICH IS WHY THIS CANNOT BE A TABLE
// OF SUFFIXES.** A house is `casa`, feminine — `la 3.ª casa` — while a degree or a place in a list is
// masculine. The indicator form (3.ª) is used rather than the spelled-out word (tercera) for the same
// reason this house writes "3rd" and not "third": beyond ten the spelled-out Spanish ordinals are
// rare enough in ordinary speech that using them reads as translated-by-machine, and past twenty they
// are actively unfamiliar. The 11/12/13 exception has NO Spanish counterpart; there is nothing to
// carry across, and a naive reuse of the English suffix table would print "3.th".
export const ORDINAL_GENDERS = ["f", "m"];
export const SPANISH_ORDINAL_INDICATORS = { f: ".ª", m: ".º" };   // ª and º, the real indicators

export function ordinalEs(n, gender = "f") {
  if (n === null || n === undefined || String(n).trim() === "") return "";
  const num = Number(n);
  if (!Number.isFinite(num)) return "";
  const g = ORDINAL_GENDERS.includes(gender) ? gender : "f";
  return `${Math.trunc(num)}${SPANISH_ORDINAL_INDICATORS[g]}`;
}

// The one function a caller in either language asks. The English branch DELEGATES to the existing law
// rather than restating it (R31: derive, so the crossing stops existing).
export function ordinalIn(code, n, gender = "f") {
  return normalizeLanguage(code) === "es" ? ordinalEs(n, gender) : ordinal(n);
}

// The house form, which is what five rooms on this site actually compose. `casa` is feminine, so the
// Spanish form is feminine and the gender is not a parameter here — it is a property of the noun.
export function ordinalHouseIn(code, n) {
  const lang = normalizeLanguage(code);
  const o = ordinalIn(lang, n, "f");
  if (!o) return "";
  return lang === "es" ? `${o} casa` : `${o} house`;
}

// ---------------------------------------------------------------------------
// THE SACRED-VERBATIM LAW. **HER ORDER, AND IT IS THE ONE THING IN ITEM 14 THAT IS NOT A PREFERENCE.**
// ---------------------------------------------------------------------------
//
// Every document this house delivers is parts. Some parts are the HOUSE TALKING ABOUT the working —
// the framing, the instructions, the meaning, the warm sentence at the end — and those translate.
// Some parts ARE the working, and those are not language, they are text: a conjuration said aloud, an
// incantation, a scripture quoted, a divine name, the letters inscribed on a seal. Translating one of
// those does not produce the same thing in another language. It produces a different thing.
//
// **DECLARED AS TWO LISTS WHOSE UNION IS THE WHOLE ROSTER**, so a part kind added later has to be put
// on one of them. The gap is the dangerous side: an unrostered kind defaulting to translatable is a
// psalm coming back paraphrased, with nothing anywhere saying so.
export const SACRED_PARTS = [
  "conjuration",    // said aloud, as given
  "incantation",
  "chant",
  "verse",          // scripture QUOTED — this house has an exact-quotation gate for a reason
  "psalm",
  "divine-name",    // a NAME, not a word (PETITION_DIVINE_NAMES is the roster it draws from)
  "seal-text",      // what is inscribed: her own roman spelling (S2.V) plus the resolver's script forms
  "petition-formula", // her closing formula, composed in CODE and never asked of a model (W12-3)
];
export const TRANSLATABLE_PARTS = [
  "title",
  "framing",        // what this working is, what it is for
  "instructions",   // how to work it — the steps AROUND the words, never the words
  "meaning",
  "materials",
  "note",
  "heading",
  "label",
];
export const DOCUMENT_PARTS = [...SACRED_PARTS, ...TRANSLATABLE_PARTS];

export function isSacredPart(kind) {
  return SACRED_PARTS.includes(String(kind ?? ""));
}

// **AN UNKNOWN PART KIND IS SACRED, NOT TRANSLATABLE, AND THAT IS THE WHOLE POINT OF THE DEFAULT.**
// R17's lesson is that the receiving side's default must not be silence; here the safe silence is
// LEAVING WORDS ALONE. Failing this way costs an untranslated line somebody can report; failing the
// other way costs a paraphrased psalm nobody can detect.
export function partTranslates(kind) {
  return TRANSLATABLE_PARTS.includes(String(kind ?? ""));
}

// The enforcement. Given the parts as they were composed and the parts as they came back translated,
// every sacred part must be byte-identical and every part must still be there.
//
// It answers a LIST of sentences rather than a boolean, because this house's gates feed the violation
// back into a re-render and a gate that says only "no" cannot say what to fix.
export function sacredVerbatimViolations(source = [], translated = []) {
  const out = [];
  const src = Array.isArray(source) ? source : [];
  const got = Array.isArray(translated) ? translated : [];

  if (got.length !== src.length) {
    out.push(`The translation has ${got.length} parts and the original has ${src.length}. Every part is kept, in order.`);
  }
  for (let i = 0; i < src.length; i += 1) {
    const a = src[i];
    const b = got[i];
    const kind = String(a?.kind ?? "");
    if (!b) { out.push(`The ${kind || "unnamed"} part is missing from the translation.`); continue; }
    if (String(b.kind ?? "") !== kind) {
      out.push(`Part ${i + 1} arrived as a ${String(b.kind ?? "unnamed")} where a ${kind || "unnamed"} was sent.`);
      // **AND THE SACRED TEST STILL RUNS ON IT.** Returning early here read as tidy and was a hole:
      // a mismatched kind and a rewritten psalm are TWO facts, and reporting only the first is what
      // let the mutation run's M6 — reading the RETURNED kind instead of the sent one — come back
      // indistinguishable from the correct law. A gate that stops at the first thing wrong with a
      // part cannot tell you the part is also not the part any more.
    }
    // The sacred test is on the kind SENT, so a translation cannot excuse itself by relabelling a
    // psalm as a note on the way back.
    if (!partTranslates(kind) && String(b.text ?? "") !== String(a.text ?? "")) {
      out.push(`The ${kind} is not translated. It is delivered exactly as it was written.`);
    }
  }
  return out;
}

// A convenience for the composer: the same law, asked one part at a time.
export function partForLanguage(part, code) {
  const kind = String(part?.kind ?? "");
  if (!partTranslates(kind)) return { ...part, text: part?.text, language: null };  // no language: it is not IN one
  return { ...part, language: normalizeLanguage(code) };
}

// The sentence a member reads when a working is delivered in Spanish with its words kept. Written
// here rather than at a call site because it is the same explanation everywhere, and the alternative
// is a member deciding for herself that the translation is broken.
export const SACRED_VERBATIM_NOTE = {
  en: "The words of the working are given exactly as they are spoken. Everything around them is in your language.",
  es: "Las palabras del trabajo se entregan tal como se pronuncian. Todo lo demás está en tu idioma.",
};
export function sacredVerbatimNote(code) {
  return SACRED_VERBATIM_NOTE[normalizeLanguage(code)];
}

// Exported so a gate can hold the English ordinal path to the law it delegates to rather than to a
// value that happens to agree today (R26).
export { ordinal, ordinalSuffix };
